import './_user-rate.dark-mode.scss';
import './_user-rate.scss';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Grid, Button } from 'react-bootstrap';
import { isEmpty, isEqualWith } from 'lodash';

import Alert from '../../../components/Alert';
import FilterGroup from '../../../components/Filters/FilterGroup';
import Modal from '../../../components/Modal/Default';
import ConfirmModal from '../../../components/Modal/Confirm';
import RouteLeavingGuard from '../../../components/Modal/RouteLeavingGuard';
import MultiSelectSearch from '../../../components/Select/MultiSearch';
import EditForm from './components/Form';
import Card from './components/Card';

import { getUserConfigs } from '../../../redux/actions/users/general';
import { getServicesListAction, onClearUserData, getCustomRatesForUserAction, onUpdateCustomRatesAction } from '../../../redux/actions/users/actions';
import { getUsersListSelector } from '../../../redux/selectors/users';
import { getRootStateSelector } from '../../../redux/selectors/root';
import * as helpers from './helpers';

const UserRate = () => {
  const formNode = useRef(null);
  const formRef = useRef(null);

  const dispatch = useDispatch();
  const params = useParams();
  const history = useHistory();

  const { config } = useSelector((state) => getRootStateSelector(state));
  const { userInfo, userRates, servicesList, servicesFlat } = useSelector((state) => getUsersListSelector(state));

  const [isDirty, setIsDirty] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingList, setIsFetchingList] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [searchList, setSearchList] = useState('');
  const [values, setValues] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [ratesState, setRatesState] = useState([]);
  const [wasAdded, setWasAdded] = useState(false);

  const toggleEdit = () => setOpenEdit(!openEdit);
  const toggleConfirm = () => setIsOpenConfirm(!isOpenConfirm);

  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      setIsFetchingList(true);
      const handler = setTimeout(() => {
        setDebouncedValue(value);
        setIsFetchingList(false);
      }, delay);
      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  const debounceValue = useDebounce(searchList, 1000);

  const filteredRates = helpers.parseUserRates(ratesState, servicesFlat, debounceValue, config);

  useEffect(() => {
    if (!params.id) {
      history.push({ pathname: '/users', search: history.location.state ? history.location.state?.query : '' });
    } else {
      document.title = 'Custom rates';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsFetching(true);
    Promise.all([dispatch(getUserConfigs()), dispatch(getServicesListAction()), dispatch(getCustomRatesForUserAction(params.id))])
      .then(() => {
        setIsFetching(false);
      })
      .catch(() => {
        history.push({ pathname: '/users', search: history.location.state ? history.location.state?.query : '' });
      });

    return () => dispatch(onClearUserData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setRatesState(userRates);

    if (userRates.length > 0) {
      setWasAdded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRates]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = 'Unsaved changes';
      return 'Unsaved changes';
    };

    isDirty && window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const isEqual = isEqualWith(ratesState, userRates, (first, second) => {
      if (first && first.length && second && second.length) {
        return first[0].price === second[0].price && first[0].percent === second[0].percent;
      }
    });

    if (ratesState.length !== userRates.length || !isEqual) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratesState]);

  const onSearchList = (e) => setSearchList(e.target.value);

  const handleSelect = (key, title) => {
    if (title === 'Edit rates') {
      toggleEdit();
    }

    if (title === 'Delete rates') {
      toggleConfirm();
    }
  };

  const onChangeCheck = (e, value, isTitle) => {
    if (e.target.value === 'checkAll' && e.target.checked) {
      return setValues([
        ...new Set([
          ...filteredRates.filter((item) => item.custom_id).map((item) => item.custom_id),
          ...filteredRates.map((item) => item.category_name),
        ]),
      ]);
    }
    if (e.target.value === 'checkAll' && !e.target.checked) {
      const ratesIdArr = filteredRates.map((rate) => rate.custom_id).filter(Boolean);
      const newValue = values.filter((item) => !ratesIdArr.includes(item));

      return setValues([...new Set(newValue)]);
    }

    if (isTitle === true && e.target.checked) {
      const ratesForCategory = filteredRates
        .filter((rate) => Number(rate.category_id) === Number(value.id) && !rate.itCategory)
        .map((rate) => rate.custom_id);
      return setValues([...new Set([...values, value.title, ...ratesForCategory])]);
    }
    if (isTitle === true && !e.target.checked) {
      const ratesForCategory = filteredRates.filter((rate) => Number(rate.category_id) === Number(value.id)).map((rate) => rate.custom_id);
      return setValues([...new Set([...values.filter((item) => item !== value.title).filter((val) => !ratesForCategory.includes(val))])]);
    }

    const fullRateByCategory = filteredRates.filter((rate) => Number(rate.category_id) === Number(value.category_id)).map((item) => item.custom_id);
    if (e.target.checked) {
      const newValues = [...values, value.custom_id];
      if (fullRateByCategory.every((v) => newValues.includes(v))) {
        return setValues([...new Set([...newValues, value.category_name])]);
      }
      setValues([...new Set(newValues)]);
    } else {
      const newValues = [...values.filter((item) => item !== value.custom_id)];
      if (!fullRateByCategory.every((v) => newValues.includes(v))) {
        return setValues([...new Set([...newValues.filter((item) => item !== value.category_name)])]);
      }
      setValues([...new Set(newValues)]);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const custom_rates = helpers.submitFormatValue(ratesState);

    setIsFetching(true);
    setErrorText('');

    try {
      await dispatch(onUpdateCustomRatesAction(params.id, { custom_rates }));
      setIsDirty(false);
      history.push({ pathname: '/users', search: history.location.state ? history.location.state?.query : '' });
    } catch (error) {
      setErrorText(error);
      setIsFetching(false);
    }
  };

  const onSubmitConfirm = () => {
    setRatesState(ratesState.filter((rate) => !values.filter(Number).includes(rate.id)));
    setValues([]);
    toggleConfirm();
  };

  const handleAddRates = (rates) => {
    setIsFetchingList(true);
    setWasAdded(true);

    setRatesState([...ratesState, ...rates]);

    setTimeout(() => {
      setIsFetchingList(false);
    }, 1000);
  };

  return (
    <Grid>
      <Modal>
        <EditForm
          showCloseButton
          toggle={toggleEdit}
          open={openEdit}
          className="modal-md"
          modalText={{
            okBtnText: 'Save changes',
            cancelBtnText: 'Cancel',
            title: 'Edit rates',
          }}
          formRef={formRef}
          inputLabel="Rate"
          values={values.filter(Number)}
          forNativeForm
          setRatesState={setRatesState}
          setIsFetchingList={setIsFetchingList}
          setValues={setValues}
          currency={config?.panel}
        />
      </Modal>

      <ConfirmModal
        toggle={toggleConfirm}
        isOpen={isOpenConfirm}
        onConfirm={onSubmitConfirm}
        title={<div className="text-center">Delete custom rates</div>}
        description={<div className="text-center">Are you sure you want to delete selected custom rates?</div>}
        confirmBtnText="Delete"
        cancelBtnText="Cancel"
      />

      <RouteLeavingGuard
        when={isDirty}
        navigate={(path) => history.push(path)}
        shouldBlockNavigation={() => {
          if (isDirty) {
            return true;
          }
          return false;
        }}
      />

      {isFetching ? (
        <div className="modal-loader" />
      ) : (
        <form onSubmit={onSubmit} className="user-rates" ref={formNode}>
          {!isEmpty(userInfo) && <div className="user-rates__title">{`${userInfo.username} (ID:${userInfo.id})`}</div>}
          <div className="user-rates__card">
            <div className="card__header">
              <FilterGroup>
                <MultiSelectSearch
                  toggleBtnTitle="Add custom rates"
                  optionsTitle="services"
                  options={helpers.parseServiceList(servicesList, config)}
                  onSubmit={handleAddRates}
                  selectedOptions={ratesState}
                />
                <li className="pull-right">
                  <div className="form-inline">
                    <label htmlFor="user-rates__search-input" className="user-rates__search__icon" />
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search"
                      autoComplete="off"
                      id="user-rates__search-input"
                      value={searchList}
                      onChange={onSearchList}
                      onKeyPress={(e) => {
                        if (e.which === 13 || e.keyCode === 13) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </div>
                </li>
              </FilterGroup>

              {errorText && (
                <div className="user-rates__alert">
                  <Alert text={errorText} />
                </div>
              )}
            </div>

            <Card
              ratesState={ratesState}
              handleSelect={handleSelect}
              onChangeCheck={onChangeCheck}
              filteredRates={filteredRates}
              values={values}
              config={config}
              setRatesState={setRatesState}
              isFetchingList={isFetchingList}
            />

            <div className="card__footer">
              <Button
                bsStyle="primary"
                disabled={(!wasAdded && ratesState.length === 0) || isFetchingList}
                type="submit"
                className="card__footer-primary"
              >
                Save changes
              </Button>
              <Button onClick={() => history.push({ pathname: '/users', search: history.location.state ? history.location.state?.query : '' })}>
                Cancel
              </Button>
            </div>
          </div>
        </form>
      )}
    </Grid>
  );
};

export default UserRate;
